/**
 * The two nested repeaters of a packing list: cartons (outer) each holding one
 * or more packed PI lines (inner).
 *
 * Owns no data fetching and no totals — the host page computes those from
 * `packingTotals`, the client mirror of the server's flattenCtns. Weights live
 * on the CARTON, not on the row: `pl_items` repeats them per row only because
 * the storage is denormalised like legacy, so summing rows would multiply a
 * carton's weight by its line count.
 *
 * Validation here is COSMETIC AND EARLY, never authoritative: it flags a
 * duplicate carton number and an unparseable weight/quantity while the clerk
 * types, using the very same helpers (`duplicateCtnNos`, `parseKg`,
 * `parseIntField`) that `draftsToInput` runs at submit. The server's
 * `duplicate_ctn_no` / `over_packed` 400s remain the source of truth, and
 * over-packing is not checked here at all — it needs the PI, which this
 * component deliberately cannot see.
 */
import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Layers, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { parseIntField } from "@/lib/moneyInput";
import {
  blankCtn,
  ctnRuns,
  duplicateCtnNos,
  parseKg,
  type CtnDraft,
  type CtnItemDraft,
} from "./packingDraft";

const inputCls =
  "h-8 w-full min-w-24 rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-60";
/** A cell the clerk must fix before submit. Semantic danger token, not the brand hue. */
const badCls = "border-danger focus:border-danger";

const cls = (extra: string, bad?: boolean) =>
  `${inputCls}${extra ? ` ${extra}` : ""}${bad ? ` ${badCls}` : ""}`;

export function CartonsEditor({
  ctns,
  onChange,
  measurementOptions,
  readOnly = false,
}: {
  ctns: CtnDraft[];
  onChange: (next: CtnDraft[]) => void;
  /** MEASUREMENT_OPTIONS from packingDraft — no settings option list exists yet.
   *  `readonly` so the `as const` constant can be passed straight through. */
  measurementOptions: readonly string[];
  /** Cosmetic gate only; records:create / records:update is enforced server-side. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  const setCtn = (i: number, patch: Partial<CtnDraft>) =>
    onChange(ctns.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const setItem = (ci: number, ii: number, patch: Partial<CtnItemDraft>) =>
    onChange(
      ctns.map((c, j) =>
        j === ci ? { ...c, items: c.items.map((it, k) => (k === ii ? { ...it, ...patch } : it)) } : c,
      ),
    );
  const removeCtn = (i: number) => onChange(ctns.filter((_, j) => j !== i));
  const removeItem = (ci: number, ii: number) =>
    onChange(ctns.map((c, j) => (j === ci ? { ...c, items: c.items.filter((_, k) => k !== ii) } : c)));
  const addCtn = () => onChange([...ctns, blankCtn(ctns)]);

  // Trimmed + case-insensitive, exactly like the server rule.
  const dupes = new Set(duplicateCtnNos(ctns).map((n) => n.trim().toUpperCase()));

  // Dải thùng giống nhau (tạo hàng loạt, yêu cầu v1.8 mục 7.1) hiện gọn một thẻ; bấm mới bung ra từng thùng. Nhớ trạng thái mở theo
  // TỪNG số thùng của dải, không theo số đầu: sửa một thùng giữa dải là tách dải làm hai, phần đuôi mang số đầu mới — khoá theo số đầu
  // thì phần đuôi tự gập lại, màn hình nhảy ngay dưới con trỏ (kiểm thử 11/10/2026).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const keysOf = (r: { from: number; to: number }) => ctns.slice(r.from, r.to + 1).map((c) => c.ctnNo.trim().toUpperCase());
  const toggleRun = (keys: string[], open: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (open) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  // Xoá cả nhóm (có thể hàng trăm thùng) phải hỏi lại.
  const [confirmRemove, setConfirmRemove] = useState<{ from: number; to: number; first: string; last: string } | null>(null);
  const runs = ctnRuns(ctns);
  const runAt = new Map(runs.map((r) => [r.from, r]));
  const blocks: ReactNode[] = [];
  for (let ci = 0; ci < ctns.length; ci++) {
    const run = runAt.get(ci);
    if (run) {
      const first = ctns[run.from]!;
      const last = ctns[run.to]!;
      const key = first.ctnNo.trim().toUpperCase();
      const keys = keysOf(run);
      const open = keys.some((k) => expanded.has(k));
      const count = run.to - run.from + 1;
      blocks.push(
        <Panel key={`run-${key}`} className="flex flex-wrap items-center gap-3 border-brand/30 bg-brand-tint/40 p-3">
          <Layers className="size-4 text-brand" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-sm font-semibold tabular text-ink">
              {t("packingBatch.runTitle", { from: first.ctnNo, to: last.ctnNo })}
            </p>
            <p className="text-xs text-ink-soft">
              {t("packingBatch.runSummary", { count, nw: first.nwKgs || "—", gw: first.gwKgs || "—" })}
              {first.measurementCm ? ` · ${first.measurementCm}` : ""}
            </p>
            {first.items.map((it, k) => (
              <p key={k} className="font-mono text-xs tabular text-ink-soft">
                {t("packingBatch.runItem", { code: it.itemNo || it.description || "—", qty: it.quantity, unit: it.unit })}
              </p>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => toggleRun(keys, open)} aria-expanded={open}>
            {open ? <ChevronDown className="size-3.5" aria-hidden="true" /> : <ChevronRight className="size-3.5" aria-hidden="true" />}
            {t(open ? "packingBatch.runCollapse" : "packingBatch.runExpand")}
          </Button>
          {!readOnly && (
            <Button variant="danger" size="sm" onClick={() => setConfirmRemove({ from: run.from, to: run.to, first: first.ctnNo, last: last.ctnNo })}>
              <Trash2 className="size-3.5" aria-hidden="true" /> {t("packingBatch.runRemove")}
            </Button>
          )}
        </Panel>,
      );
      if (!open) {
        ci = run.to;
        continue;
      }
    }
    blocks.push(renderCtn(ctns[ci]!, ci));
  }

  return (
    <div className="space-y-3">
      {ctns.length === 0 && (
        <Panel className="px-4 py-8 text-center text-sm text-ink-soft">{t("packingLists.noCtns")}</Panel>
      )}

      {blocks}
      <ConfirmDialog
        open={confirmRemove !== null}
        title={t("packingBatch.runRemoveTitle")}
        description={confirmRemove ? t("packingBatch.runRemoveConfirm", { count: confirmRemove.to - confirmRemove.from + 1, from: confirmRemove.first, to: confirmRemove.last }) : ""}
        confirmLabel={t("packingBatch.runRemove")}
        cancelLabel={t("common.cancel")}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          const r = confirmRemove;
          setConfirmRemove(null);
          if (r) onChange(ctns.filter((_, j) => j < r.from || j > r.to));
        }}
      />

      {!readOnly && (
        <Button variant="secondary" size="sm" onClick={addCtn}>
          <Plus className="size-3.5" aria-hidden="true" /> {t("packingLists.newCtn")}
        </Button>
      )}
    </div>
  );

  function renderCtn(c: CtnDraft, ci: number) {
        const nw = parseKg(c.nwKgs);
        const gw = parseKg(c.gwKgs);
        const invalidWeight=nw.value!==undefined&&gw.value!==undefined&&gw.value<nw.value;
        const duplicated = c.ctnNo.trim() !== "" && dupes.has(c.ctnNo.trim().toUpperCase());
        const badNums = [
          nw.invalid ? t("packingLists.netWeight") : null,
          gw.invalid ? t("packingLists.grossWeight") : null,
          c.items.some((it) => parseIntField(it.quantity).invalid) ? t("common.qty") : null,
        ].filter((x): x is string => x !== null);

        return (
          <Panel key={ci} className="p-3">
            <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-4 lg:grid-cols-[10rem_8rem_8rem_minmax(0,1fr)_auto]">
              <div>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-ink-faint">
                  {t("packingLists.ctnNo")}
                </label>
                <input
                  className={cls("font-mono tabular", duplicated)}
                  value={c.ctnNo}
                  disabled={readOnly}
                  aria-label={t("packingLists.ctnNo")}
                  aria-invalid={duplicated || undefined}
                  onChange={(e) => setCtn(ci, { ctnNo: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-ink-faint">
                  {t("packingLists.netWeight")}
                </label>
                <input
                  className={cls("text-right font-mono tabular", nw.invalid)}
                  inputMode="decimal"
                  value={c.nwKgs}
                  disabled={readOnly}
                  aria-label={t("packingLists.netWeight")}
                  aria-invalid={nw.invalid || undefined}
                  onChange={(e) => setCtn(ci, { nwKgs: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-ink-faint">
                  {t("packingLists.grossWeight")}
                </label>
                <input
                  className={cls("text-right font-mono tabular", gw.invalid)}
                  inputMode="decimal"
                  value={c.gwKgs}
                  disabled={readOnly}
                  aria-label={t("packingLists.grossWeight")}
                  aria-invalid={gw.invalid || undefined}
                  onChange={(e) => setCtn(ci, { gwKgs: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-ink-faint">
                  {t("packingLists.measurement")}
                </label>
                <input className={inputCls} value={c.measurementCm} disabled={readOnly} aria-label={t("packingLists.measurement")} list={`packing-sizes-${ci}`} onChange={e=>setCtn(ci,{measurementCm:e.target.value})}/>
                <datalist id={`packing-sizes-${ci}`}>{measurementOptions.map(m=><option key={m} value={m}/>)}</datalist>
              </div>
              {!readOnly && (
                <div className="flex justify-end">
                  <Button
                    variant="danger"
                    size="icon-sm"
                    onClick={() => removeCtn(ci)}
                    aria-label={t("packingLists.removeCtn")}
                    title={t("packingLists.removeCtn")}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>

            {duplicated && <p className="mt-2 text-xs text-danger">{t("errors.duplicateCtnNo")}</p>}
            {invalidWeight&&<p role="alert" className="mt-2 text-xs text-danger">{t("packingWorkspace.packing_weight_invalid")}</p>}
            {badNums.length > 0 && (
              <p className="mt-1 text-xs text-danger">
                {t("products.invalidNumber")} {badNums.join(", ")}
              </p>
            )}

            {c.items.length === 0 ? (
              <p className="mt-3 rounded-xs border border-dashed border-line-strong px-3 py-3 text-center text-xs text-ink-faint">
                {t("packingLists.emptyCtn")}
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto rounded-xs border border-line">
                <table className="w-max min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-2 [&>th]:py-1.5 [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-ink-faint">
                      <th className="w-8 text-right">#</th>
                      <th className="text-left">{t("packingLists.itemNo")}</th>
                      <th className="text-left">{t("common.subRef")}</th>
                      <th className="text-left">{t("lot.number")}</th>
                      <th className="min-w-64 text-left">{t("common.description")}</th>
                      <th className="text-left">{t("common.size")}</th>
                      <th className="text-left">{t("common.color")}</th>
                      <th className="text-left">{t("packingLists.customerPo")}</th>
                      <th className="w-24 text-right">{t("common.qty")}</th>
                      <th className="w-24 text-left">{t("common.unit")}</th>
                      {!readOnly && <th className="w-8" />}
                    </tr>
                  </thead>
                  <tbody>
                    {c.items.map((it, ii) => {
                      const qty = parseIntField(it.quantity);
                      return (
                        <tr
                          key={ii}
                          className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1 last:[&>td]:border-b-0"
                        >
                          <td className="text-right font-mono text-2xs text-ink-faint">
                            {String(ii + 1).padStart(2, "0")}
                          </td>
                          <td>
                            <input className={cls("font-mono")} value={it.itemNo} disabled
                              aria-label={t("packingLists.itemNo")}
                              onChange={(e) => setItem(ci, ii, { itemNo: e.target.value })} />
                          </td>
                          <td>
                            <input className={cls("font-mono")} value={it.subRef} disabled
                              aria-label={t("common.subRef")}
                              onChange={(e) => setItem(ci, ii, { subRef: e.target.value })} />
                          </td>
                          <td><input className={cls("font-mono")} value={it.lotNumber??""} disabled={readOnly} aria-label={`${t("lot.number")} ${ci+1}.${ii+1}`} onChange={e=>setItem(ci,ii,{lotNumber:e.target.value})}/></td>
                          <td>
                            <input className={inputCls} value={it.description} disabled={readOnly}
                              aria-label={t("common.description")}
                              onChange={(e) => setItem(ci, ii, { description: e.target.value })} />
                          </td>
                          <td>
                            <input className={inputCls} value={it.size} disabled={readOnly}
                              aria-label={t("common.size")}
                              onChange={(e) => setItem(ci, ii, { size: e.target.value })} />
                          </td>
                          <td>
                            <input className={inputCls} value={it.color} disabled={readOnly}
                              aria-label={t("common.color")}
                              onChange={(e) => setItem(ci, ii, { color: e.target.value })} />
                          </td>
                          <td>
                            <input className={cls("font-mono")} value={it.customerPo} disabled={readOnly}
                              aria-label={t("packingLists.customerPo")}
                              onChange={(e) => setItem(ci, ii, { customerPo: e.target.value })} />
                          </td>
                          <td>
                            <input className={cls("text-right font-mono tabular", qty.invalid)}
                              inputMode="numeric" value={it.quantity} disabled={readOnly}
                              aria-label={t("common.qty")}
                              aria-invalid={qty.invalid || undefined}
                              onChange={(e) => setItem(ci, ii, { quantity: e.target.value })} />
                          </td>
                          <td>
                            <input className={inputCls} value={it.unit} disabled
                              aria-label={t("common.unit")}
                              onChange={(e) => setItem(ci, ii, { unit: e.target.value })} />
                          </td>
                          {!readOnly && (
                            <td className="text-center">
                              <button
                                type="button"
                                onClick={() => removeItem(ci, ii)}
                                aria-label={t("common.removeLine")}
                                className="text-ink-faint hover:text-danger"
                              >
                                <Trash2 className="size-3.5" aria-hidden="true" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        );
  }
}
